import { useMemo, useState } from 'react';
import { UIPaginatedSelectOption } from '@/ui';
import { useFetchTeamsPaginated } from './use-fetch-teams-paginated';
import { useDebouncedValue } from '@/common/hooks';

export type FetchTeamOptionsResult = {
    options: UIPaginatedSelectOption[];
    search: string;
    setSearch: (query: string) => void;
    status: 'error' | 'success' | 'pending';
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    fetchNextPage: () => void;
    refetch: () => void;
};

/**
 * The teams of a company, as searchable pages for a picker.
 *
 * Pass `roles` to list only the teams the requesting user holds one of them in
 * — `admin` covers the inherited team-admin role, so company owners and admins
 * match every team in the company, including ones they are not a member of.
 * Every option is then one the caller may act on, so no client-side role check
 * is needed.
 *
 * The filter-options endpoint is deliberately not used here: that one backs the
 * global dashboard, tasks and reports filter bars.
 */
export function useFetchTeamOptions(params: {
    companyId: string;
    roles?: ('admin' | 'guardian' | 'memberOnly')[];
    enabled?: boolean;
}): FetchTeamOptionsResult {
    const { companyId, roles, enabled = true } = params;

    const [search, setSearch] = useState('');
    const debouncedSearch = useDebouncedValue(search.trim());

    const {
        teams,
        status,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        refetch
    } = useFetchTeamsPaginated({
        companyId,
        search: debouncedSearch,
        myTeamRoles: roles,
        // The endpoint is otherwise in insertion order, which reads as random
        // in a picker.
        sortBy: 'name',
        enabled: enabled && companyId !== ''
    });

    const options = useMemo<UIPaginatedSelectOption[]>(
        () =>
            teams.map((team) => ({
                value: team.id,
                label: team.name,
                // An unset image is an empty string, which would render as a
                // broken avatar.
                image: team.image || undefined
            })),
        [teams]
    );

    return {
        options,
        search,
        setSearch,
        status,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        refetch
    };
}
